import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { Loader2, Sparkles, X } from 'lucide-react';
import toast from 'react-hot-toast';
import { db } from '../../lib/firebase/config';
import { useAuth } from '../../context/AuthContext';
import { ClaireService } from '../../services/claireService';

type When = 'asap' | 'by' | 'at';

interface Person {
  email: string;
  name: string;
}

const todayLocal = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const INPUT = 'w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-900 bg-white focus:outline-none focus:ring-2 focus:ring-gray-200';

/** Anyone in FIN can hand Claire a task: what, how, who she should talk to, and when. */
export const GiveClaireTaskDialog: React.FC<{ open: boolean; onClose: () => void; initialInstructions?: string; onAssigned?: () => void }> = ({
  open,
  onClose,
  initialInstructions,
  onAssigned,
}) => {
  const { currentUser } = useAuth();
  const myEmail = (currentUser?.email || '').toLowerCase();
  const [people, setPeople] = useState<Person[]>([]);
  const [title, setTitle] = useState('');
  const [instructions, setInstructions] = useState('');
  const [contactEmail, setContactEmail] = useState(myEmail);
  const [when, setWhen] = useState<When>('asap');
  const [date, setDate] = useState(todayLocal());
  const [time, setTime] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && initialInstructions?.trim()) setInstructions((cur) => cur || initialInstructions.trim());
  }, [open, initialInstructions]);

  useEffect(() => {
    if (!open) return;
    setContactEmail((c) => c || myEmail);
    getDocs(query(collection(db, 'users'), where('status', '==', 'active')))
      .then((snap) =>
        setPeople(
          snap.docs
            .map((d) => ({ email: String(d.data().email || '').toLowerCase(), name: String(d.data().displayName || d.data().email || ''), isClaire: d.data().isClaire === true }))
            .filter((p) => p.email && !p.isClaire)
            .map(({ email, name }) => ({ email, name }))
            .sort((a, b) => a.name.localeCompare(b.name)),
        ),
      )
      .catch(() => setPeople([]));
  }, [open, myEmail]);

  const options = useMemo(() => {
    if (!myEmail || people.some((p) => p.email === myEmail)) return people;
    return [{ email: myEmail, name: currentUser?.displayName || myEmail }, ...people];
  }, [people, myEmail, currentUser?.displayName]);

  if (!open) return null;

  const reset = () => {
    setTitle('');
    setInstructions('');
    setContactEmail(myEmail);
    setWhen('asap');
    setDate(todayLocal());
    setTime('');
  };

  const submit = async () => {
    if (!title.trim() || !instructions.trim()) return;
    if (when === 'at' && !time) {
      toast.error('Pick a time, or choose "By a deadline" instead.');
      return;
    }
    setSaving(true);
    try {
      const res = await ClaireService.assignTask({
        title: title.trim(),
        instructions: instructions.trim(),
        contactEmail,
        when,
        ...(when === 'asap' ? {} : { date, ...(time ? { time } : {}) }),
      });
      toast.success(`Claire has it — ${res.when}.`);
      reset();
      onAssigned?.();
      onClose();
    } catch (e: any) {
      toast.error(e?.message || 'Could not give Claire the task.');
    } finally {
      setSaving(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[10000] bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="w-full max-w-lg bg-white rounded-2xl shadow-2xl p-6 space-y-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-[#b8986e]" /> Give Claire a task
            </h3>
            <p className="text-xs text-gray-500 mt-0.5">She’ll fit it into her day and message the contact person if she needs anything.</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-700">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Task title</label>
          <input className={INPUT} value={title} maxLength={200} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Add photos for the Rolex intake" autoFocus />
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Instructions</label>
          <textarea
            className={`${INPUT} min-h-[110px]`}
            value={instructions}
            maxLength={4000}
            onChange={(e) => setInstructions(e.target.value)}
            placeholder="What to do, where in FIN, and what done looks like."
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Contact person</label>
          <select className={INPUT} value={contactEmail} onChange={(e) => setContactEmail(e.target.value)}>
            {options.map((p) => (
              <option key={p.email} value={p.email}>
                {p.name}
                {p.email === myEmail ? ' (me)' : ''}
              </option>
            ))}
          </select>
          <p className="text-[11px] text-gray-400 mt-1">Who Claire talks to while she works on this.</p>
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">When</label>
          <div className="grid grid-cols-3 gap-2">
            {(
              [
                ['asap', 'As soon as possible'],
                ['by', 'By a deadline'],
                ['at', 'At a set time'],
              ] as [When, string][]
            ).map(([k, label]) => (
              <button
                key={k}
                type="button"
                onClick={() => setWhen(k)}
                className={`rounded-lg border px-2 py-2 text-xs font-medium ${when === k ? 'border-gray-900 bg-gray-900 text-white' : 'border-gray-200 text-gray-700 hover:bg-gray-50'}`}
              >
                {label}
              </button>
            ))}
          </div>
          {when !== 'asap' && (
            <div className="mt-2 flex items-center gap-2">
              <input type="date" className={`${INPUT} w-auto`} min={todayLocal()} value={date} onChange={(e) => setDate(e.target.value)} />
              <input type="time" className={`${INPUT} w-auto`} value={time} onChange={(e) => setTime(e.target.value)} />
              <span className="text-[11px] text-gray-400">{when === 'by' ? 'time optional' : 'start time'}</span>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 pt-1">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={saving || !title.trim() || !instructions.trim() || (when !== 'asap' && !date)}
            className="inline-flex items-center gap-2 px-5 py-2 text-sm font-semibold text-white bg-gray-900 rounded-lg hover:bg-gray-800 disabled:opacity-50"
          >
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
            Give to Claire
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default GiveClaireTaskDialog;
